import React, { useRef, useEffect } from "react";

import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import NavbarUI from "./components/navbar/NavbarUI";
import logo from "./images/zulal-logo.png";
import { Home } from "./spa/Home";
import { AboutZulal } from "./spa/about/AboutZulal";
import { Promotions } from "./spa/promotions/Promotions";
import { HowItWorks } from "./spa/promotions/HowItWorks";
import { UserLogin } from "./spa/user/UserLogin";
import { Page404 } from "./spa/Page404";
import { ComingSoon } from "./spa/ComingSoon";
import { Contactus } from "./spa/contactus/contactus";
import { UserHome } from "./spa/user/UserHome";

import Footer from "./spa/footer/Footer";

import { UserLayout } from "./layouts/UserLayout";
import { UserProfile1 } from "./spa/user/UserProfile1";

import { Orders } from "./spa/user/Orders";
import PartialDeliveries from "./spa/user/PartialDeliveries";
import { Logout } from "./spa/user/Logout";
import { ZulalWallet } from "./spa/user/ZulalWallet";

import { UserPrivateRoutes } from "./utils/UserPrivateRoutes";

import { Dashboard } from "./spa/user/Dashboard";
import CreditCardPage from "./spa/user/CreditCardPage";

import { UserDetails } from "./spa/user/UserDetails";

import CreateUser from "./spa/user/CreateUser";

import { TopUpConfirmation } from "./spa/user/TopUpConfirmation";
import NewsAndMediaPage from "./spa/newsandmedia/NewsAndMediaPage";
import { ProductsRange } from "./spa/shop/ProductsRange";
import { CheckOut } from "./spa/shop/CheckOut";
import { OrderConfirmationV2 } from "./spa/user/orderConfirmationV2";
import { RefundPolicy } from "./spa/RefundPolicy";
import LoyaltyCard from "./spa/user/LoyaltyCard";
import OrderTracking from "./spa/userOrderTracking/OrderTracking";
import HowItWorksMob from "./spa/promotions/HowItWorksMob";
import { DonateWater } from "./spa/shop/DonateWater";

import AddressBook from "./spa/user/AddressBook";
import { Toaster } from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import { UserPayments } from "./spa/user/UserPayments";
import { MosqueOrders } from "./spa/user/MosqueOrders";
import UserComingSoon from "./components/userComingSoon/UserComingSoon";
import { ComplaintPage } from "./spa/user/ComplaintPage";

import { useAuth } from "./context/auth.context";
import { MockOrderConfirmation } from "./spa/mock/mockorderconfirmation";

function App() {
  // Layout with Header

  const navigate = useNavigate();
  const location = useLocation(); // ✅ gives you location.pathname
  const { signOut } = useAuth();

  const isHandlingUnauthorized = useRef(false);

  useEffect(() => {
    const PROTECTED_PREFIXES = ["/user", "/checkout"];

    async function handleUnauthorized(e) {
      if (isHandlingUnauthorized.current) return;
      isHandlingUnauthorized.current = true;

      try {
        await signOut();

        const path = location.pathname || "/";
        const isProtectedRoute = PROTECTED_PREFIXES.some((p) =>
          path.startsWith(p)
        );

        if (isProtectedRoute) {
          navigate("/login", { replace: true, state: { from: path } });
        } else {
          // Public pages: do not redirect
          // console.log("401 on public route; not redirecting", e?.detail);
        }
      } finally {
        isHandlingUnauthorized.current = false;
      }
    }

    window.addEventListener("api:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("api:unauthorized", handleUnauthorized);
    };
  }, [navigate, signOut, location.pathname]);

  const WithHeaderFooterLayout = ({ children }) => (
    <>
      <Toaster />
      <ToastContainer position="top-right" />
      <NavbarUI />
      {children}
      <Footer />
    </>
  );

  // Layout without Header
  const WithoutHeaderFooterLayout = ({ children }) => <>{children}</>;

  return (
    <React.Fragment>
      <Routes>
        {/* Routes with Header and Footer */}
        <Route
          path="/"
          element={
            <WithHeaderFooterLayout>
              <Home />
            </WithHeaderFooterLayout>
          }
        />
        {/* About us Page */}
        <Route
          path="about"
          element={
            <WithHeaderFooterLayout>
              <AboutZulal />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="donatewater"
          element={
            <WithHeaderFooterLayout>
              <DonateWater />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="productsrange"
          element={
            <WithHeaderFooterLayout>
              <ProductsRange />
            </WithHeaderFooterLayout>
          }
        />

        <Route
          path="NewsMedia"
          element={
            <WithHeaderFooterLayout>
              <ComingSoon />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="mock/mockorder"
          element={
            <WithHeaderFooterLayout>
              <MockOrderConfirmation />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="ContactUs"
          element={
            <WithHeaderFooterLayout>
              <Contactus />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="ComingSoon"
          element={
            <WithHeaderFooterLayout>
              <ComingSoon />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="topupconfirmation"
          element={
            <WithHeaderFooterLayout>
              <TopUpConfirmation />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="orderconfirmation"
          element={
            <WithHeaderFooterLayout>
              <OrderConfirmationV2 />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="promotions"
          element={
            <WithHeaderFooterLayout>
              <Promotions />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="howitworks"
          element={
            <WithHeaderFooterLayout>
              <HowItWorks />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="orderTracking"
          element={
            <WithHeaderFooterLayout>
              <OrderTracking />
            </WithHeaderFooterLayout>
          }
        />

        <Route
          path="login"
          element={
            <WithHeaderFooterLayout>
              <UserLogin />
            </WithHeaderFooterLayout>
          }
        />
        <Route
          path="createuser"
          element={
            <WithHeaderFooterLayout>
              <CreateUser />
            </WithHeaderFooterLayout>
          }
        />

        <Route
          path="refundpolicy"
          element={
            <WithHeaderFooterLayout>
              <RefundPolicy />
            </WithHeaderFooterLayout>
          }
        />

        {/* Protected Routes */}

        {/* Protected Routes without Header */}
        <Route element={<UserPrivateRoutes />}>
          <Route element={<WithHeaderFooterLayout />}>
            <Route path="/user" element={<UserLayout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="userdetails" element={<UserDetails />} />
              <Route path="profile" element={<UserProfile1 />} />
              <Route path="addressbook" element={<AddressBook />} />

              <Route path="topup" element={<TopUpConfirmation />} />
              <Route path="payments" element={<UserPayments />} />
              <Route path="invoices" element={<UserComingSoon />} />
              <Route path="mosqueorders" element={<MosqueOrders />} />
              {/* <Route path="zulalwallet" element={<UserComingSoon />} /> */}
              <Route path="zulalwallet" element={<ZulalWallet />} />
              <Route path="discountcard" element={<LoyaltyCard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="partialdelieries" element={<PartialDeliveries />} />

              <Route path="creditcardpayment" element={<CreditCardPage />} />
              <Route path="dashboard" element={<UserHome />} />
              <Route path="checkout" element={<CheckOut />} />
              <Route path="complaintpage" element={<ComplaintPage />} />
              <Route path="logout" element={<Logout />} />
            </Route>
          </Route>
        </Route>
        {/* Routes with without Header and Footer */}

        <Route
          path="PromotionMob"
          element={
            <WithoutHeaderFooterLayout>
              <Promotions qry="mob" />
            </WithoutHeaderFooterLayout>
          }
        />

        <Route
          path="HowItWorksMob"
          element={
            <WithoutHeaderFooterLayout>
              <HowItWorksMob qry="mob" />
            </WithoutHeaderFooterLayout>
          }
        />

        {/* Catch-all route */}

        <Route
          path="*"
          element={
            <WithHeaderFooterLayout>
              <Page404 />
            </WithHeaderFooterLayout>
          }
        />
      </Routes>
    </React.Fragment>
  );
}

export default App;
